import React from "react";
import { Helmet } from "react-helmet-async";
import { Box, Stack, Typography, Select, TextField, MenuItem, Skeleton } from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import PreviewIcon from "@mui/icons-material/PreviewOutlined";
import ResetIcon from "@mui/icons-material/RestartAlt";

import useCaptcha from "../../../hooks/super/useCaptcha";
import useAuth from "../../../hooks/useAuth";

import PreviewModal from "./component/M_Preview";
import { CaptchaType, UserType, WafNodeType } from "../../../utils/constants";
import { CollapseAlert } from "../../../components/pages/common/styled";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import OutlinedButton from "../../../components/pages/_new/super/common/OutlinedButton";
import {
  TOKENS,
  SectionCard,
} from "../../../components/pages/_new/super/design";

function SACaptchaBlockPage() {
  const { captchaBlockPage, captchaType4Engine, getCaptchaBlockPage, updateCaptchaBlockPage, getCaptchaType, errMsg, setErr } =
    useCaptcha();
  const { isAuthenticated, adminUserType } = useAuth();

  const [type, setType] = React.useState();
  const [blockPageContent, setBlockPageContent] = React.useState();
  const changeBlockPageContent = (e) => setBlockPageContent(e.target.value);

  React.useEffect(() => {
    if (isAuthenticated) getCaptchaType(WafNodeType.WAF_ENGINE);
    return () => setErr(null);
  }, [isAuthenticated, getCaptchaType, setErr]);

  React.useEffect(() => {
    if (null === captchaType4Engine || undefined === captchaType4Engine) return;
    setType(captchaType4Engine);
  }, [captchaType4Engine]);

  const selectCaptchaType = (e) => setType(e.target.value);

  React.useEffect(() => {
    if (type === null || type === undefined) return;
    setBlockPageContent(null);
    getCaptchaBlockPage(type);
  }, [type, getCaptchaBlockPage]);

  React.useEffect(() => {
    setBlockPageContent(captchaBlockPage);
  }, [captchaBlockPage]);

  const [errOpen, setErrOpen] = React.useState(false);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  const saveBlockPageContent = () => updateCaptchaBlockPage(type, blockPageContent);
  const resetBlockPageContent = () => setBlockPageContent(captchaBlockPage);

  const [openPreview, setOpenPreview] = React.useState(false);
  const handleOpenPreview = () => setOpenPreview(true);
  const handleClosePreview = () => setOpenPreview(false);

  const canEdit = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType);
  const noContent = blockPageContent === null || blockPageContent === undefined;

  return (
    <React.Fragment>
      <Helmet title="SA Block Pages Configuration" />
      <PageHeader
        title="Captcha Block Pages"
        description="HTML shown to challenged clients — one template per captcha type"
        actions={
          <>
            <OutlinedButton
              onClick={resetBlockPageContent}
              startIcon={<ResetIcon style={{ width: 18, height: 18 }} />}
              disabled={noContent}
            >
              Reset
            </OutlinedButton>
            <OutlinedButton
              onClick={handleOpenPreview}
              startIcon={<PreviewIcon style={{ width: 18, height: 18 }} />}
              disabled={noContent}
            >
              Preview
            </OutlinedButton>
            <PrimaryButton
              onClick={saveBlockPageContent}
              startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
              disabled={noContent || !canEdit}
            >
              Save
            </PrimaryButton>
          </>
        }
      />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Stack spacing={2}>
        <SectionCard title="Captcha type" description="Select which captcha variant's block page you wish to edit">
          <Box sx={{ maxWidth: 360 }}>
            <Select
              value={type !== undefined && type}
              onChange={selectCaptchaType}
              fullWidth
              size="small"
              sx={{
                borderRadius: `${TOKENS.radiusXs}px`,
                "& .MuiOutlinedInput-input": { fontSize: 14 },
              }}
            >
              <MenuItem value={CaptchaType.HCAPTCHA}>hCaptcha</MenuItem>
              <MenuItem value={CaptchaType.RECAPTCHA_V2_CHECKBOX}>reCaptchaV2 Checkbox</MenuItem>
              <MenuItem value={CaptchaType.RECAPTCHA_V2_INVISIBLE}>reCaptchaV2 Invisible</MenuItem>
              <MenuItem value={CaptchaType.RECAPTCHA_V3}>reCaptchaV3</MenuItem>
            </Select>
          </Box>
        </SectionCard>

        <SectionCard title="Block page HTML" description="Markup served as the challenge page; edits take effect after Save">
          {noContent ? (
            <Stack spacing={1}>
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={18} variant="rectangular" sx={{ borderRadius: 1 }} />
              ))}
            </Stack>
          ) : (
            <TextField
              fullWidth
              multiline
              maxRows={18}
              minRows={15}
              value={blockPageContent || ""}
              onChange={changeBlockPageContent}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: `${TOKENS.radiusXs}px`,
                  background: TOKENS.bgSurface,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: 12.5,
                },
              }}
            />
          )}
        </SectionCard>
      </Stack>

      <PreviewModal open={openPreview} handleClose={handleClosePreview} body={blockPageContent} />
    </React.Fragment>
  );
}
export default SACaptchaBlockPage;
